import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Howl } from 'howler';
import MainTitleBar from '../MainTitleBar';
import Loading from '../Loading';
import UserPrompt from '../UserPrompt';
import UserInfo from '../UserInfo/UserInfo';
import { convertDateToUnixtTime } from '../../lib/tools';
import { updateQR } from '../../actions/api/apis';
import {
  CREATE_QRCODE,
  UPDATE_QRCODE_EXPIRE_DATE,
} from '../../actions/actionTypes';
import IncomingAlertBar from '../Remote/IncomingAlertBar';
import callSound from '../../sounds/call.mp3';
import {
  MainBlock,
  ContentsWrapper,
  Contents,
  Header,
  Content,
  Body,
  QRCode,
  NoData,
  NoDataTitle,
  IssueButton,
  UpdateExpire,
  UpdateExpireFormWrapper,
  UpdateExpireTitle,
  UpdateExpireForm,
  UpdateExpireInput,
  UpdateExpireButton,
  DeleteQRCodeButtonWrapper,
  DeleteQRCodeButton,
} from './styles/IssuePageStyles';

const soundManager = new Howl({
  src: [callSound],
  loop: true,
});

const IssuePage = ({
  isMobile,
  openUserInfo,
  userInfo,
  license,
  apiToken,
  isLoading,
  loadingMessages,
  menuIsOpen,
  currentCodeList,
  expireDateTime,
  expireDateTimeError,
  updatableDate,
  onExpire,
  version,
  callingRooms,
  allowRemoteSupport,
  innerRole,
  dispatch,
  incomingAlertTarget,
  callingRoomsMembers,
  isCompanyAdmin,
}) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const promptMessage = formatMessage({
    id: 'issue.QRCode.delete.userPrompt.button.title',
  });
  const buttonOk = formatMessage({
    id: 'issue.QRCode.delete.userPrompt.button.ok',
  });
  const buttonNo = formatMessage({
    id: 'issue.QRCode.delete.userPrompt.button.no',
  });
  const buttonArray = [
    { name: buttonOk, type: 0 },
    { name: buttonNo, type: 1 },
  ];
  const expireDateString =
    (currentCodeList && currentCodeList.convertExpiresAt) || '';
  const pngBase64 = (currentCodeList && currentCodeList.pngBase64) || '';
  const qrcode = pngBase64 ? `data:image/png;base64,${pngBase64}` : '';
  const showIncomingAlertBar =
    !isMobile &&
    allowRemoteSupport &&
    incomingAlertTarget &&
    incomingAlertTarget.appRoomId;
  if (showIncomingAlertBar && !soundManager.playing()) {
    soundManager.play();
  } else if (!showIncomingAlertBar && soundManager.playing()) {
    soundManager.stop();
  }
  return (
    <MainBlock className="mainPage">
      {isLoading && <Loading loadingMessages={loadingMessages} />}
      {openUserInfo && (
        <UserInfo
          userInfo={userInfo}
          openUserInfo={openUserInfo}
          onClose={() => dispatch({ type: 'CLOSE_USER_INFO_DIALOG' })}
        />
      )}
      <UserPrompt />
      {showIncomingAlertBar && (
        <IncomingAlertBar
          apiToken={apiToken}
          dispatch={dispatch}
          incomingAlertTarget={incomingAlertTarget}
          callingRooms={callingRooms}
        />
      )}
      <MainTitleBar
        apiToken={apiToken}
        allowRemoteSupport={allowRemoteSupport}
        isMobile={isMobile}
        menuIsOpen={menuIsOpen}
        userInfo={userInfo}
        license={license}
        version={version}
        dispatch={dispatch}
        innerRole={innerRole}
        callingRooms={callingRooms}
        callingRoomsMembers={callingRoomsMembers}
        isCompanyAdmin={isCompanyAdmin}
      />
      <ContentsWrapper>
        <Contents>
          <Header>
            <FormattedMessage id="issueInfo.issue.header" />
          </Header>
          <Body>
            <Content>
              {(qrcode && <QRCode src={qrcode} alt="qrcode" />) || (
                <NoData>
                  <NoDataTitle>
                    <FormattedMessage id="issueInfo.issue.errorMessage" />
                  </NoDataTitle>
                  <IssueButton
                    className="button"
                    id="createQRCodeButton"
                    role="presentation"
                    onClick={() => {
                      dispatch({ type: CREATE_QRCODE });
                    }}
                  >
                    <FormattedMessage id="issueInfo.issue.createButton" />
                  </IssueButton>
                </NoData>
              )}
            </Content>
            <Content>
              {expireDateString && (
                <UpdateExpire>
                  <FormattedMessage
                    id="issueInfo.issue.footer"
                    values={{ expireDateString }}
                  />
                  <UpdateExpireFormWrapper>
                    <UpdateExpireTitle>
                      <FormattedMessage id="issueInfo.issue.updateExpire" />
                      <FormattedMessage
                        id="issueInfo.issue.updatableDate"
                        values={{ updatableDate }}
                      />
                    </UpdateExpireTitle>
                    <UpdateExpireForm>
                      <UpdateExpireInput
                        error={expireDateTimeError}
                        id="createQRCodeUpdateInputText"
                        type="text"
                        placeholder="YYYY/MM/DD HH:mm"
                        defaultValue={updatableDate}
                        onChange={e => {
                          dispatch({
                            type: UPDATE_QRCODE_EXPIRE_DATE,
                            expireDateTime: e.target.value,
                          });
                        }}
                      />
                      <UpdateExpireButton
                        className="button"
                        id="createQRCodeUpdateButton"
                        role="presentation"
                        onClick={() => {
                          if (!expireDateTimeError && expireDateTime) {
                            dispatch(
                              updateQR(
                                apiToken,
                                currentCodeList.codeId,
                                convertDateToUnixtTime(expireDateTime),
                              ),
                            );
                          }
                        }}
                      >
                        <FormattedMessage id="issue.QRCode.update.button.title" />
                      </UpdateExpireButton>
                    </UpdateExpireForm>
                  </UpdateExpireFormWrapper>
                  <DeleteQRCodeButtonWrapper>
                    <DeleteQRCodeButton
                      className="button"
                      id="createQRCodeDeleteButton"
                      role="presentation"
                      onClick={() => onExpire(promptMessage, buttonArray)}
                    >
                      <FormattedMessage id="issue.QRCode.delete.button.title" />
                    </DeleteQRCodeButton>
                  </DeleteQRCodeButtonWrapper>
                </UpdateExpire>
              )}
            </Content>
          </Body>
        </Contents>
      </ContentsWrapper>
    </MainBlock>
  );
};

IssuePage.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  openUserInfo: PropTypes.bool.isRequired,
  userInfo: PropTypes.object.isRequired,
  license: PropTypes.object.isRequired,
  apiToken: PropTypes.string.isRequired,
  currentCodeList: PropTypes.object.isRequired,
  expireDateTime: PropTypes.string.isRequired,
  expireDateTimeError: PropTypes.bool.isRequired,
  updatableDate: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  loadingMessages: PropTypes.array.isRequired,
  menuIsOpen: PropTypes.bool.isRequired,
  onExpire: PropTypes.func.isRequired,
  version: PropTypes.object.isRequired,
  callingRooms: PropTypes.array.isRequired,
  allowRemoteSupport: PropTypes.bool.isRequired,
  innerRole: PropTypes.number.isRequired,
  dispatch: PropTypes.func.isRequired,
  incomingAlertTarget: PropTypes.object.isRequired,
  callingRoomsMembers: PropTypes.array.isRequired,
  isCompanyAdmin: PropTypes.bool.isRequired,
};

export default IssuePage;
